import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
    Account,
    Allocation,
    AllocationCreateRequest,
    AllocationUpdateRequest,
    AllocationWithStrategiesCreateRequest,
    AllocationWithStrategiesUpdateRequest,
    GUID,
    Strategy,
} from '@ndvr/portfolio-api';

import { PortfolioApi } from 'API';

export const useListAllocations = (enabled?: boolean) => {
    return useQuery({
        queryKey: ['allocations'],
        queryFn: async () => {
            const [response] = await PortfolioApi().getAssignableAllocations();
            return response ?? undefined;
        },
        enabled,
    });
};

export const useAllocation = (allocationGUID?: GUID<Allocation>, options?: { throwOnError?: boolean }) => {
    return useQuery({
        ...options,
        queryKey: ['allocations', allocationGUID],
        queryFn: async () => {
            const [response] = await PortfolioApi().getAllocation(allocationGUID!);
            return response ?? undefined;
        },
        enabled: !!allocationGUID,
    });
};

export const useAllocations = (
    allocationGUIDs: GUID<Allocation>[],
    options?: { enabled?: boolean; throwOnError?: boolean },
) => {
    return useQuery({
        ...options,
        queryKey: ['allocations', 'batch', [...allocationGUIDs].sort()],
        queryFn: async () => {
            if (allocationGUIDs.length === 0) {
                return [];
            }
            const [response] = await PortfolioApi().getAllocations(allocationGUIDs);
            return response ?? undefined;
        },
    });
};

export const useAllocationsByStrategy = (strategyGUID?: GUID<Strategy>) => {
    return useQuery({
        queryKey: ['allocations', 'strategy', strategyGUID],
        queryFn: async () => {
            const [response] = await PortfolioApi().getAllocationsByStrategy(strategyGUID!);
            return response ?? undefined;
        },
        enabled: !!strategyGUID,
    });
};

export const useAccountsByAllocation = (allocationGUID?: GUID<Allocation>) => {
    return useQuery({
        queryKey: ['accounts', 'allocation', allocationGUID],
        queryFn: async () => {
            const [response] = await PortfolioApi().listAccountsForAllocation(allocationGUID!);
            return response ?? undefined;
        },
        enabled: !!allocationGUID,
    });
};

export const useCreateAllocation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: AllocationCreateRequest) => {
            const [response] = await PortfolioApi().createAllocation(request);
            return response ?? undefined;
        },
        onSuccess: () => {
            toast.success('Allocation created successfully');
            queryClient.invalidateQueries({ queryKey: ['allocations'] });
            queryClient.invalidateQueries({ queryKey: ['strategies'] });
        },
        onError: () => toast.error('Failed to create allocation'),
    });
};

export const useCreateAllocationWithStrategies = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: AllocationWithStrategiesCreateRequest) => {
            const [response] = await PortfolioApi().createAllocationWithStrategies(request);
            return response ?? undefined;
        },
        onSuccess: () => {
            toast.success('Model created successfully');
            queryClient.invalidateQueries({ queryKey: ['allocations'] });
            queryClient.invalidateQueries({ queryKey: ['strategies'] });
        },
        onError: () => toast.error('Failed to create model'),
    });
};

export const useUpdateAllocation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            allocationGUID,
            request,
        }: {
            allocationGUID: GUID<Allocation>;
            request: AllocationUpdateRequest;
        }) => {
            const [response] = await PortfolioApi().updateAllocation(allocationGUID, request);
            return response ?? undefined;
        },
        onSuccess: () => {
            toast.success('Allocation updated successfully');
            queryClient.invalidateQueries({ queryKey: ['allocations'] });
            queryClient.invalidateQueries({ queryKey: ['strategies'] });
        },
        onError: () => toast.error('Failed to update allocation'),
    });
};

export const useUpdateAllocationWithStrategies = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            allocationGUID,
            request,
        }: {
            allocationGUID: GUID<Allocation>;
            request: AllocationWithStrategiesUpdateRequest;
        }) => {
            const [response] = await PortfolioApi().updateAllocationWithStrategies(allocationGUID, request);
            return response ?? undefined;
        },
        onSuccess: () => {
            toast.success('Model updated successfully');
            queryClient.invalidateQueries({ queryKey: ['allocations'] });
            queryClient.invalidateQueries({ queryKey: ['strategies'] });
        },
        onError: () => toast.error('Failed to update model'),
    });
};

export const useDeleteAllocation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (allocationGUID: GUID<Allocation>) => {
            const [response] = await PortfolioApi().deleteAllocation(allocationGUID);
            return response ?? undefined;
        },
        onSuccess: () => {
            toast.success('Allocation deleted successfully');
            queryClient.invalidateQueries({ queryKey: ['allocations'] });
            queryClient.invalidateQueries({ queryKey: ['strategies'] });
        },
        onError: (error: unknown) => {
            const errorMessage = (error as any)?.message || (error as any)?.error || '';
            if (errorMessage.toLowerCase().includes('in use') || errorMessage.toLowerCase().includes('reference')) {
                toast.error('Cannot delete allocation: It is currently being used by one or more accounts');
            } else {
                toast.error('Failed to delete allocation');
            }
        },
    });
};
